import React, { useState, useRef, useEffect } from 'react';

/**
 * Tooltip - Animated tooltip using fixed positioning
 * Based on: https://codepen.io/jh3y/pen/NWQRoXW
 * 
 * @param {ReactNode} children - Element that triggers the tooltip
 * @param {string} content - Tooltip text content
 * @param {string} placement - Tooltip position: 'top' | 'bottom' | 'left' | 'right'
 * @param {number} delay - Show delay in ms (default: 500)
 * @param {ref} triggerRef - Optional external ref for trigger element positioning
 */
const AnimatedTooltip = ({ 
  children, 
  content, 
  placement = 'top',
  delay = 500,
  triggerRef: externalTriggerRef
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isPositioned, setIsPositioned] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const timeoutRef = useRef(null);
  const internalTriggerRef = useRef(null);
  const tooltipRef = useRef(null);
  
  // Use external ref if provided, otherwise use internal
  const triggerRef = externalTriggerRef || internalTriggerRef;

  const calculatePosition = () => {
    if (!triggerRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current?.getBoundingClientRect();
    
    let top = 0;
    let left = 0;

    const gap = 4; // Gap between trigger and tooltip

    switch (placement) {
      case 'top':
        top = triggerRect.top - (tooltipRect?.height || 40) - gap;
        left = triggerRect.left + triggerRect.width / 2;
        break;
      case 'bottom':
        top = triggerRect.bottom + gap;
        left = triggerRect.left + triggerRect.width / 2;
        break;
      case 'left':
        top = triggerRect.top + triggerRect.height / 2;
        left = triggerRect.left - (tooltipRect?.width || 0) - gap;
        break;
      case 'right':
        top = triggerRect.top + triggerRect.height / 2;
        left = triggerRect.right + gap;
        break;
      default:
        break;
    }

    setPosition({ top, left });
    setIsPositioned(true);
  };

  const handleMouseEnter = () => {
    timeoutRef.current = setTimeout(() => {
      setIsVisible(true);
      setIsPositioned(false);
      // Calculate position after render
      requestAnimationFrame(() => {
        calculatePosition();
        // Trigger animation after position is calculated
        setTimeout(() => {
          setIsAnimating(true);
        }, 10);
      });
    }, delay);
  };

  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsAnimating(false);
    setIsPositioned(false);
    // Remove from DOM after animation (260ms to match transition)
    setTimeout(() => setIsVisible(false), 260);
  };

  // Recalculate on scroll/resize
  useEffect(() => {
    if (!isVisible) return;

    const handleUpdate = () => calculatePosition();
    window.addEventListener('scroll', handleUpdate, true);
    window.addEventListener('resize', handleUpdate);

    return () => {
      window.removeEventListener('scroll', handleUpdate, true);
      window.removeEventListener('resize', handleUpdate);
    };
  }, [isVisible]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  if (!content) return children;

  // Get transform based on placement and animation state
  const getTransform = () => {
    const translateCenter = 
      placement === 'top' || placement === 'bottom' 
        ? 'translateX(-50%)' 
        : 'translateY(-50%)';
    
    if (!isAnimating) {
      // Starting position (hidden)
      switch (placement) {
        case 'top':
          return `${translateCenter} translateY(8px)`;
        case 'bottom':
          return `${translateCenter} translateY(-8px)`;
        case 'left':
          return `${translateCenter} translateX(8px)`;
        case 'right':
          return `${translateCenter} translateX(-8px)`;
        default:
          return translateCenter;
      }
    }
    
    // Final position (visible)
    return translateCenter;
  };

  // Arrow rotation based on placement
  const getArrowRotation = () => {
    switch (placement) {
      case 'top':
        return 'rotate(45deg)';
      case 'bottom':
        return 'rotate(225deg)';
      case 'left':
        return 'rotate(315deg)';
      case 'right':
        return 'rotate(135deg)';
      default:
        return 'rotate(45deg)';
    }
  };

  return (
    <>
      <div 
        ref={externalTriggerRef ? null : internalTriggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className="inline-block"
      >
        {children}
      </div>
      
      {isVisible && isPositioned && (
        <div
          ref={tooltipRef}
          className="fixed z-[300] px-3 py-2 text-sm font-medium rounded-md whitespace-nowrap pointer-events-none overflow-hidden"
          style={{
            top: `${position.top}px`,
            left: `${position.left}px`,
            transform: getTransform(),
            opacity: isAnimating ? 1 : 0,
            filter: isAnimating ? 'blur(0px)' : 'blur(4px)',
            transition: 'all 0.26s ease-out',
            backgroundColor: 'rgb(229, 231, 235)', // gray-200
            color: 'rgb(0, 0, 0)', // black
            border: '1px solid rgb(156, 163, 175)', // gray-400
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          }}
        >
          <div
            style={{
              transform: isAnimating ? 'translateY(0)' : 'translateY(100%)',
              transition: 'transform 0.26s ease-out',
            }}
          >
            {content}
          </div>
        </div>
      )}
    </>
  );
};

export default AnimatedTooltip;
